import type { GameEvent, LevelDef } from '../shared/types';

/** Original, gently damped mallet synthesis. No audio files, autoplay, or network requests. */
interface Voice {
  sources: OscillatorNode[];
  nodes: AudioNode[];
}

export interface AudioDiagnostics {
  readonly enabled: boolean;
  readonly unlocked: boolean;
  readonly state: string;
  readonly musicScheduled: boolean;
  readonly voices: number;
  readonly consumedEvents: number;
  readonly soundedEvents: number;
}

const MASTER_VOLUME = 0.16;
const EIGHTH = 60 / 76 / 2;
// An original 64-step pentatonic phrase, with rests that leave conversation room.
const MELODY: ReadonlyArray<number | null> = [
  74, null, 69, null, 78, 76, null, 69,
  74, null, 71, null, 69, null, 66, null,
  69, null, 74, 76, null, 78, null, 76,
  74, null, null, 69, 66, null, null, null,
  71, null, 74, null, 78, null, 81, 78,
  76, null, 74, null, 69, null, 66, null,
  69, null, 71, 74, null, 69, null, 66,
  62, null, null, null, 69, null, null, null,
];

let context: AudioContext | null = null;
let master: GainNode | null = null;
let musicBus: GainNode | null = null;
let effectsBus: GainNode | null = null;
let enabled = false;
let unlocked = false;
let theme: LevelDef['theme'] = 'grove';
let timer: ReturnType<typeof setInterval> | null = null;
let nextNote = 0;
let step = 0;
let soundedEvents = 0;
let consumedEvents = 0;
let listeners = 0;
const voices = new Set<Voice>();
const consumed = new Set<string>();

function visible(): boolean {
  return typeof document === 'undefined' || !document.hidden;
}

function stopVoices(): void {
  for (const voice of voices) {
    for (const source of voice.sources) {
      try { source.stop(); } catch { /* It may have completed between scheduling and muting. */ }
    }
    for (const node of voice.nodes) node.disconnect();
  }
  voices.clear();
}

function stopMusic(): void {
  if (timer !== null) clearInterval(timer);
  timer = null;
}

function note(
  midi: number, when: number, duration: number, volume: number,
  bus: GainNode | null, type: 'mallet' | 'soft' = 'mallet', bend = 0,
): void {
  if (!context || !bus || voices.size >= 42) return;
  const frequency = 440 * 2 ** ((midi - 69) / 12);
  const voice: Voice = { sources: [], nodes: [] };
  voices.add(voice);
  const partials = type === 'mallet' ? [[1, 0.8, 1], [2.76, 0.13, 0.36], [4.12, 0.035, 0.15]] : [[1, 0.8, 1], [2, 0.08, 0.6]];
  partials.forEach(([ratio, amplitude, decay], index) => {
    if (!context) return;
    const source = context.createOscillator();
    const envelope = context.createGain();
    const length = duration * decay;
    source.type = 'sine';
    source.frequency.setValueAtTime(frequency * ratio, when);
    if (bend) source.frequency.exponentialRampToValueAtTime(frequency * ratio * 2 ** (bend / 12), when + Math.min(length * 0.7, 0.15));
    envelope.gain.setValueAtTime(0.00001, when);
    envelope.gain.exponentialRampToValueAtTime(Math.max(0.00002, volume * amplitude), when + 0.007);
    envelope.gain.exponentialRampToValueAtTime(0.00001, when + length);
    source.connect(envelope);
    envelope.connect(bus);
    source.start(when);
    source.stop(when + duration + 0.015);
    voice.sources.push(source);
    voice.nodes.push(source, envelope);
    if (index === 0) source.onended = () => {
      for (const node of voice.nodes) node.disconnect();
      voices.delete(voice);
    };
  });
}

function scheduleMusic(): void {
  if (!context || !enabled || !unlocked || !visible() || context.state !== 'running') return;
  const transpose = theme === 'night' ? -5 : theme === 'ruins' ? -2 : 0;
  // A backgrounded or stalled tab resumes from now instead of dumping a backlog.
  if (nextNote < context.currentTime - EIGHTH) nextNote = context.currentTime + 0.05;
  while (nextNote < context.currentTime + 0.18) {
    const midi = MELODY[step % MELODY.length];
    const swing = step % 2 ? 0.014 : 0;
    if (midi !== null) note(midi + transpose, nextNote + swing, 0.82, 0.23, musicBus);
    if (step % 8 === 0) {
      const bass = step % 32 < 16 ? 50 : 47;
      note(bass + transpose, nextNote, 1.15, 0.14, musicBus, 'soft');
      note(bass + transpose + 7, nextNote + EIGHTH * 3, 0.8, 0.075, musicBus, 'soft');
    }
    step++;
    nextNote += EIGHTH;
  }
}

function syncPlayback(): void {
  if (!context || !master) return;
  const playing = enabled && unlocked && visible() && context.state === 'running';
  master.gain.cancelScheduledValues(context.currentTime);
  master.gain.setTargetAtTime(playing ? MASTER_VOLUME : 0, context.currentTime, 0.015);
  if (playing && timer === null) {
    nextNote = context.currentTime + 0.08;
    scheduleMusic();
    timer = setInterval(scheduleMusic, 70);
  } else if (!playing) {
    stopMusic();
    stopVoices();
  }
}

/** Call directly from a click/key handler if the shell wants to unlock before mounting a canvas. */
export async function unlockAudio(): Promise<boolean> {
  if (typeof window === 'undefined' || !visible()) return false;
  try {
    if (!context || context.state === 'closed') {
      const Audio = window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Audio) return false;
      context = new Audio({ latencyHint: 'interactive' });
      master = context.createGain();
      musicBus = context.createGain();
      effectsBus = context.createGain();
      const limiter = context.createDynamicsCompressor();
      master.gain.value = 0;
      musicBus.gain.value = 0.32;
      effectsBus.gain.value = 0.72;
      limiter.threshold.value = -16;
      limiter.knee.value = 15;
      limiter.ratio.value = 3;
      limiter.attack.value = 0.008;
      limiter.release.value = 0.15;
      musicBus.connect(master);
      effectsBus.connect(master);
      master.connect(limiter);
      limiter.connect(context.destination);
      context.onstatechange = syncPlayback;
    }
    if (context.state !== 'running') await context.resume();
    unlocked = context.state === 'running';
    syncPlayback();
    return unlocked;
  } catch {
    // Safari/embedded browsers can deny a gesture. Keep silence and allow the next one.
    return false;
  }
}

/** Setting a preference never creates or resumes an AudioContext. */
export function setAudioEnabled(value: boolean): void {
  enabled = value;
  syncPlayback();
}

export function setAudioTheme(value: LevelDef['theme']): void {
  theme = value;
}

function gesture(event: Event): void {
  if (enabled && event.isTrusted && (!unlocked || context?.state !== 'running')) void unlockAudio();
}

function visibilityChanged(): void {
  syncPlayback();
}

/** Audio-only gesture listeners; game keyboard/touch controls belong entirely to the lead hook. */
export function listenForAudioUnlock(): () => void {
  if (typeof window === 'undefined') return () => {};
  listeners++;
  if (listeners === 1) {
    window.addEventListener('pointerdown', gesture, { capture: true, passive: true });
    window.addEventListener('keydown', gesture, { capture: true, passive: true });
    document.addEventListener('visibilitychange', visibilityChanged);
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    listeners--;
    if (listeners === 0) {
      window.removeEventListener('pointerdown', gesture, true);
      window.removeEventListener('keydown', gesture, true);
      document.removeEventListener('visibilitychange', visibilityChanged);
      setAudioEnabled(false);
    }
  };
}

/** The caller supplies a room/epoch/event-ID key. Muted events are consumed, never queued. */
export function playGameEvent(event: GameEvent, uniqueId: string, allowed = true): void {
  if (consumed.has(uniqueId)) return;
  consumed.add(uniqueId);
  consumedEvents++;
  if (consumed.size > 1024) consumed.delete(consumed.values().next().value!);
  if (!allowed || !enabled || !unlocked || !context || context.state !== 'running' || !visible()) return;
  const at = context.currentTime + 0.006;
  const crowd = Math.max(1, Math.sqrt(voices.size / 5));
  const mallet = (midi: number, delay = 0, duration = 0.34, volume = 0.3) => note(midi, at + delay, duration, volume / crowd, effectsBus);
  const soft = (midi: number, bend: number, duration: number, volume: number, delay = 0) => note(midi, at + delay, duration, volume / crowd, effectsBus, 'soft', bend);
  switch (event.type) {
    case 'jump': soft(63, 8, 0.15, 0.27); break;
    case 'land': soft(43, -3, 0.12, 0.19); break;
    case 'key':
      [74, 78, 81, 86].forEach((midi, i) => mallet(midi, i * 0.07, 0.57, 0.32));
      break;
    case 'switch':
      mallet(62, 0, 0.24, 0.28);
      mallet(69, 0.042, 0.36, 0.19);
      break;
    case 'enter':
      mallet(81, 0, 0.4, 0.25);
      mallet(74, 0.08, 0.55, 0.25);
      break;
    case 'complete':
      [74, 78, 81, 78, 86].forEach((midi, i) => mallet(midi, i * 0.13, 0.8, 0.3));
      break;
    case 'reset':
      // The renderer marks the engine's anonymous reset with the outcome it attributed
      // (`text`): a fall gets a soft "whoops", two notes sliding down, the second lower and
      // longer; a requested retry gets one gentle tick. Same event key, so it sounds once.
      if (event.text === 'fall') {
        soft(70, -5, 0.22, 0.21);
        soft(63, -7, 0.36, 0.18, 0.15);
      } else {
        mallet(72, 0, 0.16, 0.13);
        mallet(65, 0.05, 0.24, 0.09);
      }
      break;
    case 'start':
      [62, 69, 74].forEach((midi, i) => mallet(midi, i * 0.1, 0.48, 0.2));
      break;
    case 'emote': mallet(78, 0, 0.36, 0.19); break;
    default: return;
  }
  soundedEvents++;
}

export function getAudioDiagnostics(): AudioDiagnostics {
  return Object.freeze({
    enabled, unlocked, state: context?.state ?? 'locked', musicScheduled: timer !== null,
    voices: voices.size, consumedEvents, soundedEvents,
  });
}
